import React, { useState, useEffect } from "react";
import {
  Form,
  FormGroup,
  TextInput,
  TextArea,
  ActionGroup,
  Button,
  Switch,
  Checkbox,
  Grid,
  GridItem
} from "@patternfly/react-core";
import TrashIcon from '@patternfly/react-icons/dist/js/icons/trash-icon';

export default (props) => {
  const [productInfo, setProductInfo] = useState({
    name: props.product.name,
    link: props.product.link,
    acronyms: props.product.acronyms,
    is_draft: props.product.is_draft,
    code: props.product.code,
    related_links: props.product.related_links,
    is_layered_product: props.product.is_layered_product,
    extra_header: props.product.extra_header,
    product_extra_headers: props.product.product_extra_headers,
    show_last_minor_release: props.product.show_last_minor_release,
  });

  const [extraHeaders, setExtraHeaders] = useState(productInfo.product_extra_headers);
  const [isShowRemove, setIsShowRemove] = useState(false);
  const [originalHeaders] = useState(
    props.product.product_extra_headers.map(a => {
      let newObject = {};
      Object.keys(a).forEach(propertyKey => {
        newObject[propertyKey] = a[propertyKey];
      });
      return newObject;
    })
  );

  const [updatedProductInfo, setUpdatedProductInfo] = useState({
    id: props.product.id,
  });

  function updateProduct(key, value) {
    setProductInfo({
      ...productInfo,
      [key]: value,
    });
    setUpdatedProductInfo({
      ...updatedProductInfo,
      [key]: value,
    });
  }

  function addHeader() {
    setExtraHeaders(s => {
      return [...s, { header: "" }];
    });
  };

  function handleChange(value, index) {
    let newExtraHeaders = [...extraHeaders];
    newExtraHeaders[index].header = value;
    setExtraHeaders(newExtraHeaders);
  };

  function removeHeader(index) {
    let newExtraHeaders = [...extraHeaders];
    newExtraHeaders.splice(index, 1);
    setExtraHeaders(newExtraHeaders);
  };

  useEffect(() => {
    if (extraHeaders.length > 1) {
      setIsShowRemove(true);
    } else {
      setIsShowRemove(false);
    }
  }, [extraHeaders]);

  function renderExtraHeader() {
    if (productInfo && productInfo.is_layered_product) {
      return (
        <FormGroup
          label="Dependent Header Column"
          fieldId="product-info-extra-header"
          style={formGroupStyle}
        >
          {extraHeaders.map((item, index) => {
            return (
              <Grid hasGutter key={`grid-${index}`} style={{ marginBottom: '16px' }}>
                <GridItem span={5} key={`gridItem1-${index}`}>
                  <TextInput
                    key={`textinput-${index}`}
                    onChange={(value) => { handleChange(value, index) }}
                    value={item.header || ""}
                    id="product-info-extra-header"
                    name="product-info-extra-header"
                    type={'text'}
                  />
                </GridItem>
                {isShowRemove && (
                  <GridItem span={1} key={`gridItem2-${index}`}>
                    <Button variant="plain" onClick={() => { removeHeader(index) }} aria-label="Remove">
                      <TrashIcon />
                    </Button>
                  </GridItem>
                )}
              </Grid>
            );
          })}
          <Button onClick={addHeader}>Add</Button>
        </FormGroup>
      );
    }
  }

  function onSubmit() {
    let updateHeaders = { add: [], delete: [], update: [] };
    extraHeaders && extraHeaders.forEach((extra) => {
      if (extra.id) {
        let findIndex = originalHeaders.findIndex(
          (original) => original.id === extra.id && original.header !== extra.header
        );
        if (findIndex !== -1) {
          updateHeaders.update.push(extra);
        }
      } else {
        updateHeaders.add.push(extra.header);
      }
    });
    originalHeaders && originalHeaders.forEach((original) => {
      let findIndex = extraHeaders.findIndex(
        (extra) => extra.id === original.id
      );
      if (findIndex === -1) {
        updateHeaders.delete.push(original.id);
      }
    });

    if (updateHeaders.add.length > 0 || updateHeaders.delete.length > 0 || updateHeaders.update.length > 0) {
      props.onSubmit({ ...updatedProductInfo, "extra_headers": updateHeaders })
    } else {
      props.onSubmit({ ...updatedProductInfo })
    }
  }

  const formGroupStyle = {
    gridTemplateColumns: "300px 1fr",
  };

  return (
    <Form isHorizontal>
      <FormGroup
        label="Name"
        isRequired
        fieldId="product-info-name"
        style={formGroupStyle}
      >
        <TextInput
          value={productInfo.name || ""}
          onChange={(value) => updateProduct("name", value)}
          isRequired
          type="text"
          id="product-info-name"
          name="product-info-name"
        />
      </FormGroup>
      <FormGroup
        label="Acronyms, Short Names, Key Components"
        fieldId="product-info-acronyms"
        style={formGroupStyle}
      >
        <TextInput
          value={productInfo.acronyms || ""}
          onChange={(value) => updateProduct("acronyms", value)}
          type="text"
          id="product-info-acronyms"
          name="product-info-acronyms"
        />
      </FormGroup>
      <FormGroup
        label="Link of detailed life cycle information"
        fieldId="product-info-link"
        style={formGroupStyle}
      >
        <TextInput
          value={productInfo.link || ""}
          onChange={(value) => updateProduct("link", value)}
          type="text"
          id="product-info-link"
          name="product-info-link"
        />
      </FormGroup>
      <FormGroup
        label="Support policies"
        fieldId="product-info-top-policies"
        style={formGroupStyle}
      >
        <TextArea
          value={productInfo.related_links || ""}
          onChange={(value) => updateProduct("related_links", value)}
          name="product-info-top-policies"
          id="product-info-top-policies"
        />
      </FormGroup>
      <FormGroup
        label="Is component product"
        fieldId="product-info-is-layered-product"
        style={formGroupStyle}
      >
        <Checkbox
          label="Yes"
          aria-label="Is Component Product"
          name="product-info-is-layered-product"
          id="product-info-is-layered-product"
          isChecked={!!productInfo.is_layered_product}
          onChange={(isChecked) =>
            updateProduct("is_layered_product", isChecked)
          }
        />
      </FormGroup>
      {renderExtraHeader()}
      <FormGroup
        label="Show Last Minor Release"
        fieldId="product-show-last-minor-release"
        style={formGroupStyle}
      >
        <Switch
          id="product-show-last-minor-release"
          label={!!productInfo.show_last_minor_release ? `Enable` : `Disable`}
          labelOff={
            !!productInfo.show_last_minor_release ? `Enable` : `Disable`
          }
          isChecked={!!productInfo.show_last_minor_release}
          onChange={(isChecked) =>
            updateProduct("show_last_minor_release", isChecked)
          }
        />
      </FormGroup>
      <ActionGroup>
        <Button
          variant="primary"
          // onClick={() => props.onSubmit({ ...updatedProductInfo })}
          onClick={() => onSubmit()}
        >{`Save`}</Button>
        <Button variant="secondary" onClick={() => props.onCancel()}>
          Cancel
        </Button>
      </ActionGroup>
    </Form>
  );
};
